import { AirportLocation } from '@reward-platform/flights-schemas'
import { FLIGHT_FEATURE_FLAGS } from '@reward-platform/featureFlags'
import { useQueryAirportsList } from '~/components/search-and-results/SearchFlightForm/hooks/useQueryAirportsList'
import { ComboBox, ListBox, ListBoxItem, Paragraph } from '@alto-avios/alto-ui'
import { buildSearchCriteria } from '~/components/search-and-results/SearchFlightForm/SearchBoxUtils'
import { useFeatureFlag } from '~/context/featureFlag'
import formatAirportName from '~/utils/formatAirportName'
import style from './SearchBox.module.css'

interface SearchBoxProps {
  selectedItem?: AirportLocation
  onChange: (value?: AirportLocation) => void
  name: string
  label: string
  error?: string
  partnerId: string
  disabled?: boolean
  originAirport?: string
}

export const SearchBox = ({
  partnerId,
  selectedItem,
  onChange,
  name,
  label,
  error,
  disabled,
  originAirport,
}: Readonly<SearchBoxProps>) => {
  const { data: airportsList, isFetching: isFetchingAirports } = useQueryAirportsList({
    partnerId,
    enabled: !disabled,
    originAirport,
  })
  const supportsExtendedSearch = useFeatureFlag(FLIGHT_FEATURE_FLAGS.extendedAirportsSearch)
  const isDisabled = disabled || (!!originAirport && isFetchingAirports)

  return (
    <ComboBox
      data-testid="search-airport-input"
      label={label}
      isRequired
      isDisabled={isDisabled}
      name={name}
      formValue="key"
      className={style.search_box}
      onFocus={(e) => e.target.select()}
      selectedKey={selectedItem?.airportCode ?? null}
      onSelectionChange={(key) => {
        const airport = airportsList?.find(({ airportCode }) => airportCode === key)

        if (airport) {
          onChange(airport)
        }
      }}
      isInvalid={!!error}
      errorMessage={error}
      menuTrigger="focus"
      isLoading={!airportsList}
    >
      {airportsList ? (
        <ListBox selectionMode="single" selectionBehavior="replace">
          {airportsList.map((item) => {
            const airportLabel = formatAirportName(item)
            return supportsExtendedSearch ? (
              <ListBoxItem
                key={item.airportCode}
                id={item.airportCode}
                textValue={buildSearchCriteria(item)}
              >
                <Paragraph size="md">{airportLabel}</Paragraph>
                <Paragraph size="xs">{`${item.cityName}, ${item.countryName}`}</Paragraph>
              </ListBoxItem>
            ) : (
              <ListBoxItem key={item.airportCode} id={item.airportCode} textValue={airportLabel}>
                {airportLabel}
              </ListBoxItem>
            )
          })}
        </ListBox>
      ) : null}
    </ComboBox>
  )
}
